import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import { dataUrlToFile } from "../../../../../shared/helpers/data-url-to-file";
import { getAssetTypes } from "../../../../../store/slices/asset-type";
import {
  addDriverModel,
  deleteDriverModel,
  downloadDriverModelFile,
  editDriverModel,
  getDriverModels,
  getDriverModelsByCode,
} from "../../../../../store/slices/driver-model.slice";
import { getSLS } from "../../../../../store/slices/sls.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";
import { Spinner } from "react-bootstrap";
import {
  imageFileTypes,
  validateImageFile,
} from "src/shared/helpers/validate-Files";
import "../../../admin.scss";

interface DriverModelTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
  driverSearchValue: string;
}

const DriverModelTableComponent: FC<DriverModelTableComponentProps> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
  driverSearchValue,
}) => {
  const formRef = createRef();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [slsSupplierOptions, setSlsSupplierOptions] = useState<any>([]);
  const [assetTypeOptions, setAssetTypeOptions] = useState<any>([]);
  const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
    useState<number>(0);
  const [initialValues, setInitialValues] = useState<any>({
    driverModelCode: "",
    ratedWatt: "",
    supplierId: "",
    expectedLifeTime: "",
    picture: "",
    assetTypeId: "",
    description: "",
    remark: "",
  });

  const [preview, setPreview] = useState<any>();
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [isShowImage, setIsShowImage] = useState<boolean>(false);

  // const [isPdfDownload, setIsPdfDownload] = useState<boolean>(false);
  const [isExcelDownload, setIsExcelDownload] = useState<boolean>(false);

  const dispatch = useDispatch();

  const driverModelDetails = useSelector((state: any) => {
    return state.driverModel.driverModelDetails;
  });

  const isLoadingdriverModelDownload = useSelector((state: any) => {
    return state.driverModel.isLoadingdriverModelDownload;
  });

  const isLoadingDriverModelDetails = useSelector((state: any) => {
    return state.driverModel.isLoadingDriverModelDetails;
  });

  const slsSupplierResponse = useSelector((state: any) => {
    return state.sls.sls;
  });

  const isLoadingSls = useSelector((state: any) => {
    return state.sls.isLoadingSls;
  });

  const assetTypeResponse = useSelector((state: any) => {
    return state.assetType.assetTypes;
  });

  const isLoadingAssetType = useSelector((state: any) => {
    return state.assetType.isLoading;
  });

  useEffect(() => {
    const sls = slsSupplierResponse?.data?.map((sl: any) => ({
      label: sl?.name,
      value: sl?.id,
    }));

    const assetType = assetTypeResponse?.data
      ?.filter((item: any) => item?.name?.toLowerCase() === "luminarie") // Filter only 'luminarie'
      ?.map((item: any) => ({
        label: item?.name,
        value: item?.id,
      }));

    setSlsSupplierOptions(sls);
    setAssetTypeOptions(assetType);
  }, [slsSupplierResponse, assetTypeResponse]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setIsDetails(false);
    setIsDelete(false);
    setIsShowImage(false);

    setInitialValues({
      driverModelCode: "",
      ratedWatt: "",
      supplierId: "",
      expectedLifeTime: "",
      picture: "",
      assetTypeId: "",
      description: "",
      remark: "",
    });
  };

  const detailsAction = (driverModelCode: any) => {
    setIsDetails(true);
    // setIsModalOpen(true);

    setIsShowImage(true);
    setIsEdit(false);
    setIsDelete(false);

    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
      dispatch(getDriverModelsByCode({ driverModelCode })),
    ])
      .then(() => {
        setIsModalOpen(true);
      })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });
  };

  const editAction = (driverModelCode: any) => {
    setIsDetails(false);
    // setIsModalOpen(true);
    setIsEdit(true);
    setIsDelete(false);

    setIsShowImage(true);

    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
      dispatch(getDriverModelsByCode({ driverModelCode })),
    ])
      .then(() => {
        setIsModalOpen(true);
      })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });

    setSelectedIdToDeleteOrEdit(driverModelCode);
  };

  const deleteAction = (driverModelCode: any) => {
    setIsDetails(false);
    setIsModalOpen(true);
    setIsEdit(false);
    setIsDelete(true);

    setSelectedIdToDeleteOrEdit(driverModelCode);
  };

  const handleDelete = () => {
    dispatch(
      deleteDriverModel({ driverModelCode: selectedIdToDeleteOrEdit })
    ).then(() => {
      onRefetchData();
      handleCloseModal();
    });
  };

  const handleAddDriverModel = () => {
    Promise.all([
      dispatch(getSLS({ PageNumber: 0, pageSize: 0 })),
      dispatch(getAssetTypes({ PageNumber: 0, pageSize: 0 })),
    ])
      .then(() => {
        setIsDetails(false);
        setIsModalOpen(true);
        setIsEdit(false);
        setIsDelete(false);
        setIsShowImage(false);
      })
      .catch((error: any) => {
        console.log("Error in api call: ", error);
      });
  };

  useEffect(() => {
    if (!driverModelDetails || !Object.keys(driverModelDetails).length) return;

    setInitialValues({
      driverModelCode: driverModelDetails?.driverModelCode || "",
      ratedWatt: driverModelDetails?.ratedWatt || "",
      supplierId: driverModelDetails?.supplierId || "",
      expectedLifeTime: driverModelDetails?.expectedLifeTime || "",
      picture: driverModelDetails?.picture || "",
      assetTypeId: driverModelDetails?.assetTypeId || "",
      description: driverModelDetails?.description || "",
      remark: driverModelDetails?.remark || "",
    });

    if (driverModelDetails?.picture === null) {
      setSelectedFile(null);
      setIsShowImage(false);
    } else if (driverModelDetails?.picture) {
      const dataUrl = driverModelDetails?.picture || "";
      const filename = "image.png";
      const file = dataUrlToFile(dataUrl, filename);

      setSelectedFile(file);
      setIsShowImage(true);
    }
  }, [driverModelDetails]);

  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      setSelectedFile(null);
      return;
    }

    const isValid = await validateImageFile(event.target.files[0]);
    if (!isValid) {
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(event.target.files[0]);
  };

  const onSubmit = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();
      const {
        assetTypeId,
        driverModelCode,
        expectedLifeTime,
        ratedWatt,
        supplierId,
        // picture,
        description,
        remark,
      } = form.values;

      if (
        !driverModelCode ||
        !assetTypeId ||
        !expectedLifeTime ||
        !ratedWatt ||
        !supplierId
      ) {
        return;
      }

      if (
        driverModelCode?.length > 50 ||
        expectedLifeTime <= 0 ||
        expectedLifeTime > 1000000000 ||
        ratedWatt <= 0 ||
        ratedWatt > 1000 ||
        description?.length > 200 ||
        remark?.length > 200
      ) {
        return;
      }

      if (isEdit) {
        const formValues = form.values;

        const payload = {
          ...formValues,
          driverModelCode: selectedIdToDeleteOrEdit,
        };

        dispatch(
          editDriverModel({
            payload,
            driverModelCode: selectedIdToDeleteOrEdit,
            file: selectedFile || null,
          })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
        });
        setSelectedFile(null);
      } else {
        dispatch(
          addDriverModel({ payload: form.values, file: selectedFile || null })
        ).then(() => {
          handleCloseModal();
          onRefetchData();
        });
        setSelectedFile(null);
      }
    }
  };

  const getDriverModelModalActionConfig = (): any => {
    if (isDetails) return [];
    if (isDelete) {
      return [
        {
          label: "Yes",
          event: handleDelete,
          variant: "primary",
        },
      ];
    }
    return [
      {
        label: "Save",
        event: onSubmit,
        variant: "primary",
      },
    ];
  };

  const modalActionConfig = getDriverModelModalActionConfig();

  const actionConfig: any = [
    {
      label: "Edit",
      actionProperty: "driverModelCode",
      event: editAction,
      variant: "secondary",
      className: "mx-2 my-1",
      moduleName: "Driver Model",
      actionName: "Edit-Driver Model",
      icon: faPencilAlt,
    },
    {
      label: "View",
      actionProperty: "driverModelCode",
      event: detailsAction,
      variant: "info",
      className: "mx-2 my-1",
      moduleName: "Driver Model",
      actionName: "View-Driver Model",
      icon: faFileAlt,
    },
    {
      label: "Delete",
      actionProperty: "driverModelCode",
      event: deleteAction,
      variant: "danger",
      className: "mx-2 my-1",
      moduleName: "Driver Model",
      actionName: "Delete-Driver Model",
      icon: faTrashAlt,
    },
  ];

  const emptyConfig = {
    title: "No Driver Model Found",
    description: "",
  };

  const formConfig: FormConfig = {
    controls: [
      {
        name: "driverModelCode",
        label: "Driver Model",
        placeholder: "LDM 005",
        type: "text",
        required: !isDetails,
        readOnly: isDetails || isEdit,
        disabled: isDetails || isEdit,
      },
      {
        name: "ratedWatt",
        label: "Rated Watt",
        placeholder: "20",
        type: "number",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "supplierId",
        label: "Supplier",
        placeholder: "Select Supplier",
        type: "searchable-select",
        options: slsSupplierOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "expectedLifeTime",
        label: "Expected Lifetime",
        placeholder: "10000",
        type: "number",
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "picture",
        label:
          "Picture",
        placeholder: "Choose file",
        type: "file",
        onChange: handleFile,
        filterFileTypes: imageFileTypes,
        // required: true,
        info: "Supported file types: jpg, jpeg, png",
      },
      {
        name: "assetTypeId",
        label: "Asset Type",
        placeholder: "Select Asset Type",
        type: "select",
        options: assetTypeOptions,
        required: !isDetails,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "description",
        label: "Description",
        placeholder: "Something...",
        type: "textarea",
        required: false,
        readOnly: isDetails,
        disabled: isDetails,
      },
      {
        name: "remark",
        label: "Remarks",
        placeholder: "Something...",
        type: "textarea",
        required: false,
        readOnly: isDetails,
        disabled: isDetails,
      },
    ],
  };

  const validationSchema = yup.object().shape({
    driverModelCode: yup
      .string()
      .required("Driver Model is required")
      .max(50, "Driver Model should not exceed 50 characters"),
    ratedWatt: yup
      .number()
      .min(1, "Minimum value should be greater than 0")
      .required("Rated Watt is required")
      .max(1000, "Rated Watt should not exceed 1000 watts"),
    supplierId: yup.string().required("Supplier is required"),
    expectedLifeTime: yup
      .number()
      .min(1, "Minimum value should be greater than 0")
      .required("Expected Lifetime is required")
      .max(1000000000, "Expected Lifetime should not exceed 1000000000 hours"),
    assetTypeId: yup.string().required("Asset Type is required"),
    description: yup
      .string()
      .max(200, "Description should not exceed 200 characters"),
    remark: yup.string().max(200, "Remarks should not exceed 200 characters"),
    // picture: yup.string().required("Picture is required"),
  });

  const onRemoveFile = () => {
    setSelectedFile(null);

    setInitialValues({
      ...driverModelDetails,
      picture: null,
    });

    setIsShowImage(false);
  };

  const onClickDownload = (fileType: any) => {
    dispatch(
      downloadDriverModelFile({
        fileType,
        tcCode: null,
        siteCode: null,
        blockCode: null,
        searchText: driverSearchValue || "",
        sortBy: null,
        sortDirection: "ASC",
      })
    );
  };

  const getDriverModelModalHeaderTitle = () => {
    if (isEdit) return "Edit Driver Model";
    if (isDetails) return "Driver Model Details";
    if (isDelete) return "Delete Driver Model";
    return "Add Driver Model";
  };

  return (
    <div>
      <div>
        <div className="d-flex gap-3 justify-content-end table-generic-action table-toolbar">
          <div>
            <SearchBarComponent
              placeholder="Search Driver Model"
              methodToDispatch={getDriverModels}
              getSearch={onSearch}
            />
          </div>

          <div className="d-flex gap-3 align-items-center asset-driver-model-button-group">
            <div className="export ">
              <HasPermission
                moduleName="Driver Model"
                actionName="Add-Driver Model"
                isRoute={false}
              >
                <div className="d-flex gap-2">
                  {/* <B2Button
                    className="icon-text-btn ms-2"
                    variant="primary"
                    onClick={() => {
                      onClickDownload("downloadPdf");
                      setIsPdfDownload(true);
                      setIsExcelDownload(false);
                    }}
                  >
                    {isLoadingdriverModelDownload && isPdfDownload ? (
                      <Spinner
                        animation="border"
                        variant="info"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                    )}
                    Export as PDF
                  </B2Button> */}

                  <B2Button
                    className="icon-text-btn"
                    variant="primary"
                    onClick={() => {
                      onClickDownload("downloadExcel");
                      setIsExcelDownload(true);
                      // setIsPdfDownload(false);
                    }}
                  >
                    {isLoadingdriverModelDownload && isExcelDownload ? (
                      <Spinner
                        animation="border"
                        variant="info"
                        size="sm"
                        className="me-2"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                    )}
                    {/* Export as Excel */}
                    Export
                  </B2Button>
                </div>
              </HasPermission>
            </div>
            <HasPermission
              moduleName="Driver Model"
              actionName="Add-Driver Model"
              isRoute={false}
            >
              <B2Button
                className="icon-text-btn"
                variant="primary"
                onClick={handleAddDriverModel}
              >
                {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
                Add New Driver Model
              </B2Button>
            </HasPermission>
          </div>
        </div>
        <div className="my-3">
          <TableComponent
            tableHeaders={columns} // Action used
            emptyConfig={emptyConfig}
            data={data}
            totalNumberOfRecords={totalNumberOfRecords}
            pageSize={pageSize}
            current={current}
            onChangePage={onChangePage}
            actionConfig={actionConfig}
            onChangeSort={onChangeSort}
            isLoadingData={isLoading}
          />
        </div>
      </div>

      {/* modals */}
      {isModalOpen && (
        <ModalComponent
          isConformationModal={isDelete}
          showModal={isModalOpen}
          handleClose={handleCloseModal}
          headerTitle={getDriverModelModalHeaderTitle()}
          actionConfig={modalActionConfig}
        >
          {isDelete ? (
            <h5 className="text-center">
              Are you sure to <b className="text-danger">Delete</b>?
            </h5>
          ) : (
            <SkeletonComponent
              wrapperHeight="480px"
              skeletonHeight="50px"
              isLoading={
                isLoadingAssetType ||
                isLoadingDriverModelDetails ||
                isLoadingSls
              }
              count={8}
            >
              <FormBuilderComponent
                formRef={formRef}
                initialValues={initialValues}
                validation={validationSchema}
                formConfig={formConfig}
                fileConfig={{
                  isShowImage,
                  isEdit,
                  onRemoveFile: onRemoveFile,
                  preview,
                }}
              />
            </SkeletonComponent>
          )}
        </ModalComponent>
      )}
    </div>
  );
};

export default DriverModelTableComponent;
